import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Section, Container, Eyebrow, RevealHeading, Reveal } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { ServiceGrid } from "@/components/ServiceGrid";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { DIGITAL } from "@/data/site";
import { PRACTICE_SERVICES } from "@/data/services";

export default function Digital() {
  const d = DIGITAL;
  return (
    <>
      <Seo
        title="Digital | GovX Nigeria"
        description="GovX Digital builds technology around real government, political and citizen problems — delivery platforms, citizen engagement, political infrastructure and governance intelligence."
      />
      <PageHero {...d.hero} kicker="GovTech — Nigeria" />

      {/* Products */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Eyebrow dark>Solutions</Eyebrow>
              <RevealHeading
                lines={["Built Around", "Real Problems."]}
                as="h2"
                className="mt-7 font-display text-4xl font-medium tracking-tight sm:text-5xl"
              />
            </div>
            <Reveal delay={0.1}>
              <p className="max-w-sm text-sm leading-relaxed text-govx-grey">
                Technology is the infrastructure. Impact is the outcome. We build systems staff and
                citizens actually use.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {d.products.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden border border-white/10 bg-govx-charcoal/40 p-8 transition-colors duration-500 hover:border-govx-lime/50"
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-2xl font-medium tracking-tight text-govx-paper">{p.title}</h3>
                  <ArrowRight className="h-5 w-5 text-govx-grey transition-all duration-300 group-hover:translate-x-1 group-hover:text-govx-lime" />
                </div>
                <p className="mt-3 text-sm text-govx-grey">{p.sub}</p>
                <div className="mt-8 flex flex-wrap items-center gap-2">
                  {p.steps.map((s, j, a) => (
                    <span key={s} className="flex items-center gap-2">
                      <span className="border border-white/10 bg-govx-black px-3 py-1.5 font-mono text-[0.7rem] uppercase tracking-wider text-govx-paper/80">
                        {s}
                      </span>
                      {j < a.length - 1 && <span className="text-govx-green">→</span>}
                    </span>
                  ))}
                </div>
                {/* faux dashboard bars */}
                <div className="mt-8 flex items-end gap-1.5 opacity-70">
                  {[40, 65, 30, 80, 55, 70, 45, 90].map((h, k) => (
                    <motion.span
                      key={k}
                      initial={{ height: 0 }}
                      whileInView={{ height: h }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: 0.2 + k * 0.05 }}
                      className={`w-full ${k === 7 ? "bg-govx-lime" : "bg-govx-green/60"}`}
                      style={{ maxHeight: 90 }}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Tech stack */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>Explore Digital</Eyebrow>
          <RevealHeading
            lines={["Digital Capabilities."]}
            as="h2"
            className="mt-7 font-display text-3xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-12">
            <ServiceGrid slugs={PRACTICE_SERVICES.Digital} />
          </div>
        </Container>
      </Section>

      {/* Tech stack */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <Eyebrow dark>The Stack</Eyebrow>
          <RevealHeading
            lines={["Technology That", "Removes Friction."]}
            as="h2"
            className="mt-7 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-5xl text-govx-paper"
          />
          <div className="mt-14 grid grid-cols-3 gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-3 lg:grid-cols-3">
            {d.stack.map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group flex items-center justify-between bg-govx-black px-6 py-8 transition-colors duration-300 hover:bg-govx-charcoal"
              >
                <span className="font-display text-xl font-medium tracking-tight text-govx-paper">{s}</span>
                <span className="font-mono text-[0.62rem] text-govx-grey group-hover:text-govx-lime">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Digital"
        title={["Technology should make", "government work better."]}
        cta="Talk to GovX Digital"
      />
    </>
  );
}
